import { parsePatchFiles } from "@pierre/diffs"
import { AlertTriangle, ArrowRight, CheckCircle2, Move, ScanSearch } from "lucide-react"
import { useMemo } from "react"
import type { ChangedFileInfo, GetDifftasticResponse, GetFilePairResponse, GetFilePatchResponse } from "../../nestAPIs/git"
import { GIT_DIFF_STRATEGIES, type GitDiffStrategy } from "../../utils/gitDiffStrategy"
import { type CommentHandlers, FileDiffViewer, MultiFileDiffViewer } from "../FilesAndDiffs"
import { analyzeDiff, changedLineCount, type DiffLabAnalysis, type DiffLabChange, type DiffLabMove, sliceRange } from "./diffAnalysis"
import { parseDifftasticAnsi, type DifftasticLineKind, type DifftasticTone } from "./difftasticAnsi"

export type DiffLabStrategy = GitDiffStrategy
export const DIFF_LAB_STRATEGIES = GIT_DIFF_STRATEGIES

interface DiffStrategyViewProps {
    strategy: DiffLabStrategy
    file: ChangedFileInfo
    pair: GetFilePairResponse | null
    patch: GetFilePatchResponse | null
    loading: boolean
    error: string | null
    diffStyle: "split" | "unified"
    difftastic: GetDifftasticResponse | null
    difftasticLoading: boolean
    pairElapsedMs: number | null
    patchElapsedMs: number | null
    difftasticRoundTripMs: number | null
    commentHandlers?: CommentHandlers | null
    showTimings?: boolean
}

const MAX_FOCUSED_CARDS = 40

const DIFFTASTIC_TONE_CLASS: Record<DifftasticTone, string> = {
    default: "text-base-content",
    muted: "text-muted",
    removed: "text-error",
    added: "text-success",
    accent: "text-warning",
}

const DIFFTASTIC_LINE_CLASS: Record<DifftasticLineKind, string> = {
    context: "",
    removed: "bg-error/10",
    added: "bg-success/10",
    changed: "bg-warning/10",
}

function rangeLabel(range: { startLineNumber: number; endLineNumberExclusive: number }): string {
    const end = range.endLineNumberExclusive - 1
    if (end < range.startLineNumber) return `at L${range.startLineNumber}`
    if (end === range.startLineNumber) return `L${range.startLineNumber}`
    return `L${range.startLineNumber}–${end}`
}

function FocusedComparison({
    file,
    before,
    after,
    change,
    diffStyle,
}: {
    file: ChangedFileInfo
    before: string
    after: string
    change: DiffLabChange
    diffStyle: "split" | "unified"
}) {
    const oldSlice = sliceRange(before, change.original, 2)
    const newSlice = sliceRange(after, change.modified, 2)
    return (
        <MultiFileDiffViewer
            oldFile={{ name: `${file.oldPath ?? file.path} · L${oldSlice.startLine}–${oldSlice.endLine}`, contents: oldSlice.contents }}
            newFile={{ name: `${file.path} · L${newSlice.startLine}–${newSlice.endLine}`, contents: newSlice.contents }}
            diffStyle={diffStyle}
            commentHandlers={null}
        />
    )
}

function MoveComparison({
    file,
    before,
    after,
    move,
    diffStyle,
}: {
    file: ChangedFileInfo
    before: string
    after: string
    move: DiffLabMove
    diffStyle: "split" | "unified"
}) {
    const oldSlice = sliceRange(before, move.original)
    const newSlice = sliceRange(after, move.modified)
    const changed = move.nestedChanges.length > 0
    return (
        <section className="overflow-hidden border border-border bg-base-100">
            <div className="flex flex-wrap items-center gap-2 border-b border-border bg-base-200 px-3 py-2 text-xs">
                <Move size={14} className={changed ? "text-warning" : "text-success"} />
                <span className="font-medium text-base-content">{changed ? "Moved with edits" : "Moved unchanged"}</span>
                <span className="font-mono text-muted">{rangeLabel(move.original)}</span>
                <ArrowRight size={12} className="text-muted" />
                <span className="font-mono text-muted">{rangeLabel(move.modified)}</span>
                {changed && <span className="ml-auto text-warning">{move.nestedChanges.length} nested change block(s)</span>}
            </div>
            <MultiFileDiffViewer
                oldFile={{ name: `${file.oldPath ?? file.path} · ${rangeLabel(move.original)}`, contents: oldSlice.contents }}
                newFile={{ name: `${file.path} · ${rangeLabel(move.modified)}`, contents: newSlice.contents }}
                diffStyle={diffStyle}
                commentHandlers={null}
            />
        </section>
    )
}

function ReviewLens({
    file,
    pair,
    diffStyle,
    ignoreTrimWhitespace,
    pairElapsedMs,
    showTimings,
}: {
    file: ChangedFileInfo
    pair: GetFilePairResponse
    diffStyle: "split" | "unified"
    ignoreTrimWhitespace: boolean
    pairElapsedMs: number | null
    showTimings: boolean
}) {
    const analysis = useMemo(() => analyzeDiff(pair.before, pair.after, ignoreTrimWhitespace), [ignoreTrimWhitespace, pair.after, pair.before])
    const strictAnalysis = useMemo<DiffLabAnalysis | null>(
        () => (ignoreTrimWhitespace ? analyzeDiff(pair.before, pair.after, false) : null),
        [ignoreTrimWhitespace, pair.after, pair.before]
    )
    const ignoredLines = strictAnalysis ? Math.max(0, changedLineCount(strictAnalysis.allChanges) - changedLineCount(analysis.allChanges)) : 0
    const visibleResidual = analysis.residualChanges.slice(0, MAX_FOCUSED_CARDS)
    const hiddenResidualCount = analysis.residualChanges.length - visibleResidual.length
    const analysisElapsedMs = analysis.elapsedMs + (strictAnalysis?.elapsedMs ?? 0)

    return (
        <div className="flex min-h-full flex-col gap-4 bg-editor-background p-4">
            <div className={showTimings ? "grid gap-2 sm:grid-cols-5" : "grid gap-2 sm:grid-cols-3"}>
                <Metric label="Moved blocks" value={analysis.moves.length} />
                <Metric label="Moved with edits" value={analysis.moves.filter((move) => move.nestedChanges.length > 0).length} />
                <Metric label="Residual blocks" value={analysis.residualChanges.length} emphasize />
                {showTimings && <Metric label="Lens compute" value={`${analysisElapsedMs.toFixed(1)} ms`} />}
                {showTimings && <Metric label="Snapshot RPC" value={formatElapsed(pairElapsedMs)} />}
            </div>
            {ignoreTrimWhitespace && (
                <div className="flex items-center gap-2 border border-border bg-base-100 px-3 py-2 text-xs text-muted">
                    <ScanSearch size={14} />
                    Trim-only whitespace is ignored.{" "}
                    {ignoredLines > 0 ? `${ignoredLines} changed line(s) were removed from this view.` : "No trim-only churn was found."}
                </div>
            )}
            {analysis.hitTimeout && (
                <div className="flex items-center gap-2 border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
                    <AlertTriangle size={14} /> The 250 ms analysis budget was reached; review the Current strategy as the complete source of truth.
                </div>
            )}
            {analysis.moveDetectionError && (
                <div className="flex items-center gap-2 border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
                    <AlertTriangle size={14} /> Move detection failed safely; the residual line diff is still complete. Use Current as the source of truth.
                </div>
            )}

            {analysis.moves.length > 0 && (
                <section className="flex flex-col gap-3">
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Recognized moves</h2>
                    {analysis.moves.map((move, index) => (
                        <MoveComparison
                            key={`${move.original.startLineNumber}:${move.modified.startLineNumber}:${index}`}
                            file={file}
                            before={pair.before}
                            after={pair.after}
                            move={move}
                            diffStyle={diffStyle}
                        />
                    ))}
                </section>
            )}

            <section className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Residual changes</h2>
                    <span className="text-xs text-muted">Changes not explained as moves</span>
                </div>
                {visibleResidual.length === 0 ? (
                    <div className="flex items-center gap-2 border border-success/30 bg-success/10 px-3 py-3 text-sm text-success">
                        <CheckCircle2 size={16} /> No residual edits remain in this analysis.
                    </div>
                ) : (
                    visibleResidual.map((change, index) => (
                        <section
                            key={`${change.original.startLineNumber}:${change.modified.startLineNumber}:${index}`}
                            className="overflow-hidden border border-border bg-base-100"
                        >
                            <div className="flex items-center gap-2 border-b border-border bg-base-200 px-3 py-2 text-xs">
                                <span className="font-medium text-base-content">Change {index + 1}</span>
                                <span className="font-mono text-muted">{rangeLabel(change.original)}</span>
                                <ArrowRight size={12} className="text-muted" />
                                <span className="font-mono text-muted">{rangeLabel(change.modified)}</span>
                            </div>
                            <FocusedComparison file={file} before={pair.before} after={pair.after} change={change} diffStyle={diffStyle} />
                        </section>
                    ))
                )}
                {hiddenResidualCount > 0 && (
                    <div className="border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
                        {hiddenResidualCount} additional residual blocks are omitted from this experimental focused view. Use Current for the complete patch.
                    </div>
                )}
            </section>
        </div>
    )
}

function Metric({ label, value, emphasize = false }: { label: string; value: string | number; emphasize?: boolean }) {
    return (
        <div className="border border-border bg-base-100 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-muted">{label}</div>
            <div className={emphasize ? "mt-0.5 text-lg font-semibold text-warning" : "mt-0.5 text-lg font-semibold text-base-content"}>{value}</div>
        </div>
    )
}

function formatElapsed(elapsedMs: number | null): string {
    return elapsedMs === null ? "—" : `${elapsedMs.toFixed(1)} ms`
}

export function DiffStrategyView({
    strategy,
    file,
    pair,
    patch,
    loading,
    error,
    diffStyle,
    difftastic,
    difftasticLoading,
    pairElapsedMs,
    patchElapsedMs,
    difftasticRoundTripMs,
    commentHandlers = null,
    showTimings = true,
}: DiffStrategyViewProps) {
    const parsedPatch = useMemo(() => {
        if (!patch?.patch) return null
        try {
            for (const parsed of parsePatchFiles(patch.patch, file.path)) {
                if (parsed.files[0]) return parsed.files[0]
            }
        } catch (cause) {
            console.error("[DiffLab] Failed to parse Git patch", cause)
        }
        return null
    }, [file.path, patch?.patch])
    const difftasticLines = useMemo(() => parseDifftasticAnsi(difftastic?.output ?? ""), [difftastic?.output])

    if (error) return <div className="flex h-full items-center justify-center p-6 text-sm text-error">{error}</div>
    if (loading || (strategy === "difftastic" && difftasticLoading)) {
        return <div className="flex h-full items-center justify-center p-6 text-sm text-muted">Loading comparison…</div>
    }
    if (file.binary) return <div className="flex h-full items-center justify-center p-6 text-sm text-muted">Binary files are not rendered in Diff Lab.</div>

    if (strategy === "current") {
        if (!parsedPatch) return <div className="flex h-full items-center justify-center p-6 text-sm text-muted">No patch content for this file.</div>
        return (
            <div className="min-h-full bg-editor-background">
                {showTimings && <TimingBar label="Git patch RPC" elapsedMs={patchElapsedMs} />}
                <FileDiffViewer fileDiff={parsedPatch} diffStyle={diffStyle} disableFileHeader disableWorkerPool commentHandlers={commentHandlers} />
            </div>
        )
    }

    if (!pair || pair.tooLarge) {
        return (
            <div className="flex h-full items-center justify-center p-6 text-sm text-muted">The complete file snapshots are too large for this strategy.</div>
        )
    }

    if (strategy === "whole-file") {
        return (
            <div className="min-h-full bg-editor-background">
                {showTimings && <TimingBar label="Snapshot RPC" elapsedMs={pairElapsedMs} />}
                <MultiFileDiffViewer
                    oldFile={{ name: file.oldPath ?? file.path, contents: pair.before }}
                    newFile={{ name: file.path, contents: pair.after }}
                    diffStyle={diffStyle}
                    expandUnchanged
                    commentHandlers={commentHandlers}
                />
            </div>
        )
    }

    if (strategy === "move-aware" || strategy === "low-noise") {
        return (
            <ReviewLens
                file={file}
                pair={pair}
                diffStyle={diffStyle}
                ignoreTrimWhitespace={strategy === "low-noise"}
                pairElapsedMs={pairElapsedMs}
                showTimings={showTimings}
            />
        )
    }

    if (!difftastic?.available) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
                <div className="text-sm font-medium text-base-content">Difftastic is not available</div>
                <div className="max-w-md text-xs text-muted">
                    Install the `difft` binary on the computer running Nest. The other strategies remain fully functional.
                </div>
            </div>
        )
    }
    if (difftastic.error) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
                <div className="text-sm font-medium text-error">Structural preview failed</div>
                <div className="max-w-lg text-xs text-muted">{difftastic.error}</div>
            </div>
        )
    }
    return (
        <div className="min-h-full bg-editor-background p-4">
            <div className="mb-3 flex items-center justify-between gap-3 text-xs text-muted">
                <span>Tree-sitter structural alignment · raw Difftastic presentation</span>
                <span>
                    {showTimings ? `${difftastic.elapsedMs} ms engine · ${formatElapsed(difftasticRoundTripMs)} round trip` : `${difftastic.elapsedMs} ms`}
                </span>
            </div>
            {difftastic.truncated && (
                <div className="mb-3 border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">Output was truncated at 1 MiB.</div>
            )}
            {difftastic.output ? (
                <pre className="overflow-x-auto border border-border bg-base-100 py-3 font-mono text-xs leading-5">
                    <code>
                        {difftasticLines.map((line, lineIndex) => (
                            <span key={lineIndex} className={`block min-w-max px-4 ${DIFFTASTIC_LINE_CLASS[line.kind]}`} data-difftastic-line={line.kind}>
                                {line.segments.length === 0
                                    ? " "
                                    : line.segments.map((segment, segmentIndex) => (
                                          <span
                                              key={segmentIndex}
                                              className={`${DIFFTASTIC_TONE_CLASS[segment.tone]}${segment.bold ? " font-semibold" : ""}${segment.dim ? " opacity-70" : ""}`}
                                          >
                                              {segment.text}
                                          </span>
                                      ))}
                            </span>
                        ))}
                    </code>
                </pre>
            ) : (
                <div className="border border-border bg-base-100 p-4 font-mono text-xs text-muted">No structural changes.</div>
            )}
        </div>
    )
}

function TimingBar({ label, elapsedMs }: { label: string; elapsedMs: number | null }) {
    return (
        <div className="flex items-center justify-between gap-3 border-b border-border bg-base-100 px-3 py-2 text-xs text-muted">
            <span>{label}</span>
            <span>{formatElapsed(elapsedMs)}</span>
        </div>
    )
}
